/**
 * Service for Operations Inbox operations
 * Handles bulk resolution, snoozing, and location assignment
 */

// Use a generic type for postgrest client to avoid version-specific typing issues
type PostgrestClientLike = {
  from: (table: string) => any;
  rpc: (fn: string, params?: any) => any;
};

export type BulkResolveResult = {
  pending_tenant_id: string;
  tail_number: string;
  success: boolean;
  message: string;
};

export const InboxService = {
  /**
   * Bulk resolve quarantine items to the same aircraft
   */
  async bulkResolveAircraft(
    postgrest: PostgrestClientLike,
    pendingTenantIds: string[],
    aircraftId: string
  ): Promise<BulkResolveResult[]> {
    const { data, error } = await postgrest.rpc('bulk_resolve_quarantine', {
      p_pending_tenant_ids: pendingTenantIds,
      p_aircraft_id: aircraftId,
    });

    if (error) throw new Error(error.message);
    return data || [];
  },

  /**
   * Snooze an inbox item
   */
  async snoozeItem(
    postgrest: PostgrestClientLike,
    options: {
      pendingTenantId?: string;
      tripId?: string;
      fboId: string;
      snoozeUntil: Date;
    }
  ): Promise<string> {
    const { data, error } = await postgrest.rpc('snooze_inbox_item', {
      p_pending_tenant_id: options.pendingTenantId || null,
      p_trip_id: options.tripId || null,
      p_snooze_until: options.snoozeUntil.toISOString(),
      p_fbo_id: options.fboId,
    });

    if (error) throw new Error(error.message);
    return data;
  },

  /**
   * Unsnooze an inbox item (set snooze_until to null)
   */
  async unsnoozeItem(
    postgrest: PostgrestClientLike,
    options: {
      pendingTenantId?: string;
      tripId?: string;
      fboId: string;
    }
  ): Promise<string> {
    const { data, error } = await postgrest.rpc('snooze_inbox_item', {
      p_pending_tenant_id: options.pendingTenantId || null,
      p_trip_id: options.tripId || null,
      p_snooze_until: null,
      p_fbo_id: options.fboId,
    });

    if (error) throw new Error(error.message);
    return data;
  },

  /**
   * Assign trip to hangar (creates placement with full trip duration)
   */
  async assignToHangar(
    postgrest: PostgrestClientLike,
    tripId: string,
    hangarId: string,
    arrivalDate: string,
    departureDate: string
  ): Promise<void> {
    // Delete existing placements
    await postgrest
      .from('trip_placement')
      .delete()
      .eq('trip_id', tripId);

    // Create new placement
    const { error } = await postgrest.from('trip_placement').insert({
      trip_id: tripId,
      hangar_id: hangarId,
      ramp_id: null,
      start_time: arrivalDate,
      end_time: departureDate,
      placement_source: 'manual',
    });

    if (error) throw new Error(error.message);
  },

  /**
   * Assign trip to ramp (creates placement with position)
   */
  async assignToRamp(
    postgrest: PostgrestClientLike,
    tripId: string,
    rampId: string,
    arrivalDate: string,
    departureDate: string,
    x: number,
    y: number,
    angle: number
  ): Promise<void> {
    // Delete existing placements
    await postgrest
      .from('trip_placement')
      .delete()
      .eq('trip_id', tripId);

    // Create new placement
    const { error } = await postgrest.from('trip_placement').insert({
      trip_id: tripId,
      hangar_id: null,
      ramp_id: rampId,
      start_time: arrivalDate,
      end_time: departureDate,
      x,
      y,
      angle,
      placement_source: 'manual',
    });

    if (error) throw new Error(error.message);
  },

  /**
   * Resolve a single quarantine item with FAA mapping
   * (Same logic as QuarantineService.resolveWithFaaMapping but returns void)
   */
  async resolveAircraft(
    postgrest: PostgrestClientLike,
    pendingTenantId: string,
    aircraftId: string
  ): Promise<void> {
    const results = await InboxService.bulkResolveAircraft(
      postgrest,
      [pendingTenantId],
      aircraftId
    );

    const result = results[0];
    if (!result?.success) {
      throw new Error(result?.message || 'Failed to resolve aircraft');
    }
  },
};
